import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function useAdminPushSubscription() {
  const { data: vapidData } = useQuery<{ publicKey: string | null }>({
    queryKey: ["/api/push/vapid-key"],
  });

  useEffect(() => {
    if (!vapidData?.publicKey) return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      console.log("[Push Admin] Push not supported in this browser");
      return;
    }

    const vapidKey = vapidData.publicKey;

    async function subscribePush() {
      try {
        if (Notification.permission === "denied") {
          console.log("[Push Admin] Notifications denied by user");
          return;
        }

        if (Notification.permission === "default") {
          const result = await Notification.requestPermission();
          console.log("[Push Admin] Permission result:", result);
          if (result !== "granted") return;
        }

        const registration = await navigator.serviceWorker.ready;
        let subscription = await registration.pushManager.getSubscription();

        if (subscription) {
          const existingKey = subscription.options?.applicationServerKey;
          if (existingKey) {
            const existingKeyBase64 = arrayBufferToBase64(existingKey);
            if (existingKeyBase64 !== vapidKey) {
              console.log("[Push Admin] VAPID key changed, resubscribing");
              await subscription.unsubscribe();
              subscription = null;
            }
          }
        }

        if (!subscription) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidKey),
          });
          console.log("[Push Admin] Created new subscription");
        }

        const json = subscription.toJSON();
        await apiRequest("POST", "/api/admin/push/subscribe", {
          endpoint: json.endpoint,
          keys: {
            p256dh: json.keys?.p256dh,
            auth: json.keys?.auth,
          },
        });
        console.log("[Push Admin] Subscription registered with server");
      } catch (err) {
        console.error("[Push Admin] Subscription error:", err);
      }
    }

    subscribePush();
  }, [vapidData?.publicKey]);
}
